import {
  DEFAULT_PRODUCT,
  PLATFORM_NAME,
  type Product,
  productName,
} from '@app/shared/brand'

/** What a mark can stand for: a product, or the platform holding them. */
export type Mark = Product | 'platform'

/**
 * A mark. One drawing each, in one vocabulary: square, hairline, no curve and
 * no fill, built from the same grid as everything else here.
 *
 * **They must be tellable apart at 16px**, because the app switcher shows them
 * together, so each differs from the others in something that survives that
 * size — an outline's aspect, a base line, whether it is one shape or four.
 *
 * ## ShulBase: an aron kodesh, drawn as its two doors meeting at a seam.
 *
 * Chosen after the obvious candidates failed for reasons worth recording, so
 * nobody re-walks them. A sunrise says *zmanim*, and this is a shul's whole back
 * office rather than a times viewer. An arch on a plinth is a matzeiva
 * silhouette. Strokes rising from a base — including a literal ש — read as a W,
 * because a monoline geometric shin is indistinguishable from Cyrillic Ш. A
 * room-and-bimah floor plan reads as a stop button.
 *
 * The aron survives all of it. Two doors and a seam is a shape nothing else in
 * software claims, it is unmistakable to the people who use this, and it is
 * built from the same square hairline vocabulary as everything else here — no
 * curve, no fill. The handles are load-bearing: without them a divided
 * rectangle is a window.
 *
 * **It is responsive, and that is the point of the handles being optional.**
 * Below `DETAIL_FROM` they are smaller than a stroke can render and turn the
 * seam into a smudge, so they drop and the mark keeps only what survives: case,
 * seam, base. Detail that cannot be seen is not detail, it is noise.
 *
 * Proportions are square by design — the case is 16×16 and the base sits just
 * under it, so the whole mark reads square in any square container rather than
 * as an upright shape floating in one.
 *
 * ## Studio: a daf — the block of text, and the commentary around it.
 *
 * The page a sefer is set on, drawn as what makes it that page: an inner block
 * with the margins around it. It is unmistakable against the aron because the
 * two are built the opposite way — the aron is a case DIVIDED by a seam and
 * standing on a base, this is a square set INSIDE a square and standing on
 * nothing. Concentric against divided survives 16px, where a stroke's worth of
 * detail does not.
 *
 * The two rules reaching in from the margins are its optional detail, the way
 * the handles are the aron's: the commentary meeting the block. They are
 * deliberately unequal — a daf is not symmetrical, and a mark that mirrors
 * reads as a diagram rather than as a page.
 *
 * ## Terumah: a pushka, and the gift going into it.
 *
 * A box whose lid is broken by a slot, and a stroke standing in the slot. It
 * is told from the aron because nothing divides it and it stands on nothing,
 * and from the daf because what is inside it comes from above rather than
 * sitting at its center. A broken lid survives 16px; the gift above the slot is
 * its optional detail, and without it the box is still a box with a slot.
 *
 * ## Adanim: four squares in a field.
 *
 * The platform is not a thing you use, so its mark draws what it IS — more than
 * one product under one arrangement — rather than an object. It is the shape
 * the app already uses for the platform surface in its nav, so the front door
 * and the switcher say the same thing.
 *
 * `apps/web/public/favicon.svg`, `favicon-studio.svg`, `favicon-terumah.svg`
 * and `favicon-platform.svg` carry the same geometries in their compact form.
 * Change one, change both.
 */

/**
 * Below this the handles are sub-pixel; above it they are the mark's tell.
 *
 * 20 rather than 24 deliberately: the real call sites are 20, 22 and 24, and a
 * threshold at 24 would split them — the marketing header would carry a
 * different mark from the app for a two-pixel difference. The only thing that
 * should land on the compact form is the 16px favicon.
 */
const DETAIL_FROM = 20

/**
 * Thinner as it grows. A stroke that stays constant in user units gets visually
 * heavier the larger it renders, so the weight comes down to keep the mark
 * looking like the same drawing at 16px and at 64.
 */
function strokeFor(size: number): number {
  if (size >= 48) return 1.1
  if (size >= 32) return 1.25
  if (size >= DETAIL_FROM) return 1.4
  return 1.6
}

export function Logo({
  size = 24,
  product = DEFAULT_PRODUCT,
}: {
  size?: number
  product?: Mark
}) {
  const detailed = size >= DETAIL_FROM
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role="img"
      style={{ color: 'var(--text-heading)' }}
    >
      <title>
        {product === 'platform' ? PLATFORM_NAME : productName(product)}
      </title>
      <g
        stroke="currentColor"
        strokeWidth={strokeFor(size)}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {product === 'platform' ? (
          <>
            <rect x="3" y="3" width="7.5" height="7.5" />
            <rect x="13.5" y="3" width="7.5" height="7.5" />
            <rect x="3" y="13.5" width="7.5" height="7.5" />
            <rect x="13.5" y="13.5" width="7.5" height="7.5" />
          </>
        ) : product === 'terumah' ? (
          <>
            <polyline points="10,9 4,9 4,20 20,20 20,9 14,9" />
            {detailed ? <line x1="12" y1="3.5" x2="12" y2="11" /> : null}
          </>
        ) : product === 'studio' ? (
          <>
            <rect x="3.5" y="3.5" width="17" height="17" />
            {/* The block the daf is set around. */}
            <rect x="8.5" y="8.5" width="7" height="7" />
            {detailed ? (
              <>
                <line x1="3.5" y1="8.5" x2="8.5" y2="8.5" />
                <line x1="15.5" y1="15.5" x2="20.5" y2="15.5" />
              </>
            ) : null}
          </>
        ) : (
          <>
            <rect x="4" y="4" width="16" height="16" />
            <line x1="12" y1="4" x2="12" y2="20" />
            {detailed ? (
              <>
                {/* Out near the middle of each door, where a handle actually
                    sits — tucked against the seam they read as a thickening
                    of it. */}
                <line x1="8.6" y1="10.8" x2="8.6" y2="13" />
                <line x1="15.4" y1="10.8" x2="15.4" y2="13" />
              </>
            ) : null}
            {/* The step it stands on — and the product is called ShulBase, so
                the object and the name land on the same drawing. */}
            <line x1="2.5" y1="22" x2="21.5" y2="22" />
          </>
        )}
      </g>
    </svg>
  )
}
